import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { BaseView, BaseText } from '@/components/base'
import { AmountAndConversion, AppButton } from '@/components'
import { splitRequestIcon } from '@/assets/images'
import { useAppPreferencesStore } from '@/store/useAppPreferencesStore'
import { useSplitTransactionStore } from '@/store/useSplitTransactionStore'
import useOffsets from '@/hooks/useOffsets'

interface RequestResult {
  recipientId: string
  recipientName: string
  success: boolean
  error?: string
}

interface SplitSuccessParams {
  results: RequestResult[]
  successCount: number
  failureCount: number
  amountPerPerson: number
  initiatorAmount: number
  currency: string
  relativeFiatFormatted: string
  originalAmount: string
  type: 'split'
}

export const SplitRequestSuccessScreen: React.FC = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const offsets = useOffsets()
  const displayCurrency = useAppPreferencesStore(
    (state) => state.displayCurrency,
  )
  const clearSplit = useSplitTransactionStore((s) => s.clearSplit)

  const params = location.state as SplitSuccessParams | undefined

  // Clear split state on mount
  React.useEffect(() => {
    clearSplit()
  }, [clearSplit])

  if (!params) {
    // Redirect to home if no params
    React.useEffect(() => {
      navigate('/dashboard', { replace: true })
    }, [navigate])
    return null
  }

  const {
    results,
    successCount,
    amountPerPerson,
    currency,
    relativeFiatFormatted,
  } = params

  const handleDone = () => {
    navigate('/activity')
  }

  const handleViewRequests = () => {
    navigate('/requests')
  }

  // Get names of successful recipients
  const successfulRecipientNames = results
    .filter((result) => result.success)
    .map((result) => result.recipientName)
    .join(', ')

  return (
    <BaseView className="flex-1" style={{ backgroundColor: '#141417' }}>
      {/* Main Content Container - Full height with flex */}
      <BaseView
        className="flex-1 px-3"
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          paddingTop: offsets.top - 30,
        }}
      >
        {/* Centered content that takes available space */}
        <BaseView
          className="items-center flex-1"
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            gap: '48px',
            marginTop: '60px',
          }}
        >
          {/* Split Request Icon - exactly 180px like the regular success page */}
          <img
            src={splitRequestIcon}
            alt="Split request"
            className="object-contain"
            style={{
              width: '180px',
              height: '180px',
            }}
          />

          {/* Content Section with 24px gap */}
          <BaseView className="items-center" style={{ gap: '24px' }}>
            {/* Title - 24px font size */}
            <BaseText
              className="font-bold text-center"
              style={{
                fontSize: '24px',
                color: '#FFFFFF',
              }}
            >
              {t('translation:splitRequestsSent', {
                defaultValue: 'Split requests sent',
              })}
            </BaseText>

            {/* Amount Display - using AmountAndConversion component */}
            <AmountAndConversion
              cryptoAmount={{
                amount: String(amountPerPerson),
                code: currency,
              }}
              fiatAmount={{
                amount: relativeFiatFormatted?.replace(/[^\d.,]/g, '') || '0',
                code: displayCurrency?.isin || 'USD',
              }}
              showDecimals={true}
              shouldMaskStablecoinAsFiat={false}
            />

            {/* Display successful recipient names */}
            {successfulRecipientNames && (
              <BaseText
                fontFamily="medium"
                className="text-center"
                color="label.description"
                style={{
                  fontSize: '16px',
                }}
              >
                {t('translation:toRecipients', {
                  defaultValue: 'to {{names}}',
                  names: successfulRecipientNames,
                })}
              </BaseText>
            )}
          </BaseView>
        </BaseView>
      </BaseView>

      {/* Fixed Bottom Buttons */}
      <BaseView
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '16px',
          paddingBottom: `${offsets.bottom + 16}px`,
          backgroundColor: '#131415',
        }}
      >
        <BaseView
          gap={12}
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {successCount > 0 && (
            <AppButton
              label={t('translation:viewRequests', {
                defaultValue: 'View requests',
              })}
              onPress={handleViewRequests}
              type="secondaryGray"
              containerStyle={{
                whiteSpace: 'nowrap',
              }}
            />
          )}
          <AppButton
            label={t('form:actions.done', { defaultValue: 'Done' })}
            onPress={handleDone}
            type="primaryGradient"
          />
        </BaseView>
      </BaseView>
    </BaseView>
  )
}

export default SplitRequestSuccessScreen

